import { logger } from './logger';

export interface WaitForIframeOptions {
  selector: string;           // CSS selector for iframe
  expectedSrc: string;        // Expected src URL
  timeoutMs: number;          // Maximum wait time (default: 5000)
  pollIntervalMs: number;     // Polling interval (default: 50)
}

/**
 * Wait for iframe src to be set correctly with polling
 * If timeout reached, applies workaround by setting src manually
 *
 * @example
 * await waitForIframeSrc({
 *   selector: '#checkout-frame iframe',
 *   expectedSrc: 'https://...',
 *   timeoutMs: 5000
 * });
 */
export async function waitForIframeSrc(options: WaitForIframeOptions): Promise<boolean> {
  const {
    selector,
    expectedSrc,
    timeoutMs = 5000,
    pollIntervalMs = 50
  } = options;

  const startTime = Date.now();
  let pollCount = 0;

  while (Date.now() - startTime < timeoutMs) {
    pollCount++;

    const iframe = document.querySelector(selector) as HTMLIFrameElement;

    if (!iframe) {
      logger.warn(`[WaitForIframe] Iframe not found (poll ${pollCount})`);
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
      continue;
    }

    const currentSrc = iframe.src;
    const isBlank = currentSrc === 'about:blank' || !currentSrc;

    if (!isBlank && currentSrc && currentSrc.includes(expectedSrc)) {
      logger.info(`[WaitForIframe] Iframe src set correctly by library after ${Date.now() - startTime}ms`);
      return true;
    }

    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
  }

  // Timeout reached - apply workaround
  const iframe = document.querySelector(selector) as HTMLIFrameElement;

  if (!iframe) {
    logger.error(`[WaitForIframe] Iframe not found after ${timeoutMs}ms timeout`);
    return false;
  }

  const currentSrc = iframe.src;
  const isBlank = currentSrc === 'about:blank' || !currentSrc;

  logger.warn(
    `[WaitForIframe] Timeout reached (${timeoutMs}ms, ${pollCount} polls). ` +
    `Applying workaround. Current src: ${currentSrc || 'empty'}`
  );

  if (isBlank) {
    iframe.src = expectedSrc;
    logger.info(`[WaitForIframe] Workaround applied - src set manually to: ${expectedSrc}`);
    return true;
  }

  return true;
}
